import React, { useRef } from 'react';
import styled from 'styled-components';

import { useModal } from '../../../Providers/ModalContext';
import Modal from '../../../Global/Modal';
import RichText from '../../../Global/RichText';

import CloseIcon from '../../../../Images/icons/close-icon.png';
import ServicesBg from '../../../../Images/background/bg_production-services.png';
import LogoStudio from '../../../../Images/logos/logo-studio.png';

import TravelIcon from '../../../../Images/gallery/production-services/travel-icon.png';
import LocationsIcon from '../../../../Images/gallery/production-services/locations-icon.png';
import CastingIcon from '../../../../Images/gallery/production-services/casting-icon.png';
import PermitsIcon from '../../../../Images/gallery/production-services/permits-icon.png';
import RentIcon from '../../../../Images/gallery/production-services/rent-icon.png';
import CrewIcon from '../../../../Images/gallery/production-services/crew-icon.png';
import InsuranceIcon from '../../../../Images/gallery/production-services/insurance-icon.png';
import AllIcon from '../../../../Images/gallery/production-services/all-icon.png';

import {
  Services_Title,
  Travel_Text,
  Locations_Text,
  Casting_Text,
  Permits_Text,
  Rent_Text,
  Crew_Text,
  Insurance_Text,
  All_Text,
} from '../../../../utils/ES/Modals/productionServices';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
  desktop: '1440px',
};

const CloseContainer = styled.div`
  padding: 10px;
  position: absolute;
  top: 30px;
  right: 30px;

  @media (max-width: ${breakpoints.mobile}) {
    top: 15px;
    right: 15px;
    width: 60px;
    height: 60px;
  }
`;

const CloseButton = styled.img`
  cursor: pointer;
  width: 64px;
  height: auto;
  transition: all 0.3s ease-out;
  transform-origin: center;

  @media (max-width: ${breakpoints.mobile}) {
    width: 40px;
  }

  &:hover {
    filter: drop-shadow(0px 0px 10px rgba(255, 255, 255, 1))
      drop-shadow(0px 0px 4px rgba(255, 255, 255, 1));
    transform: scale(1.15);
  }
`;

const ModalContent = styled.div`
  width: 100%;
  position: relative;
  padding: 25px 60px;
  display: flex;
  align-items: flex-start;

  flex-direction: column;
  gap: 4rem;

  p,
  h2,
  h3 {
    text-transform: uppercase;

    &.sub-text {
      text-transform: none;
    }
  }
`;

const TitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: 3.375rem;
  padding-right: 5%;

  p,
  h2,
  h3 {
    text-transform: uppercase;
  }
`;

const LogoContent = styled.div`
  display: block;
  max-width: 276px;
  width: 100%;
  padding: 15px;
  position: relative;

  img {
    display: block;
    width: 100%;
    cursor: pointer;
    transition: all 0.3s ease-out;

    &:hover {
      filter: drop-shadow(0px 0px 20px rgba(250, 115, 19, 1));
    }
  }

  &::after {
    content: '';
    position: absolute;
    width: 12px;
    height: 93px;
    background: #fff;

    top: 0;
    bottom: 0;

    margin: auto;
    right: -10%;
  }
`;

const ServicesContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  row-gap: 4rem;
  column-gap: 1.3rem;

  @media (max-width: ${breakpoints.mobile}) {
    column-gap: 0px;
    row-gap: 2rem;
  }
`;

const ServiceItem = styled.div`
  max-width: ${({ $maxWidth = '336px' }) => $maxWidth};
  width: 100%;
  height: ${({ $itemHeight = '292px' }) => $itemHeight};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 50%;
    height: 210px;
    gap: 2rem;
  }

  p {
    text-align: center;
    height: 84px;
    display: flex;
    align-items: center;
    justify-content: center;

    transition: all 0.3s ease-out;
  }

  &:hover {
    p {
      text-shadow: 0px 0px 12px rgba(255, 167, 43, 1);
      font-weight: 700;
    }

    img {
      filter: drop-shadow(0px 0px 12px rgba(255, 167, 43, 1))
        drop-shadow(0px 0px 4px rgba(255, 167, 43, 1));
    }
  }
`;

const ServicesImage = styled.img`
  max-width: ${({ $imgWidth = '200px' }) => $imgWidth};
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;

  transition: all 0.3s ease-out;

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 100%;
    width: auto;
    padding: 0 20%;
    height: 80px;
  }
`;

const ModalProductionServices = () => {
  const { closeModal } = useModal();

  return (
    <>
      <Modal id="production-services" bgImg={ServicesBg}>
        <CloseContainer>
          <CloseButton
            onClick={closeModal}
            aria-label="Close modal"
            src={CloseIcon}
          />
        </CloseContainer>
        <ModalContent>
          <TitleContent>
            <LogoContent>
              <img
                src={LogoStudio}
                onClick={closeModal}
                aria-label="Close modal"
                alt="Kuter Studio"
              />
            </LogoContent>
            <RichText dataText={Services_Title} />
          </TitleContent>
          <ServicesContent>
            <ServiceItem>
              <ServicesImage src={TravelIcon} alt="Travel & Living" />
              <RichText dataText={Travel_Text} />
            </ServiceItem>
            <ServiceItem>
              <ServicesImage
                src={LocationsIcon}
                $imgWidth={'109px'}
                alt="Locations"
              />
              <RichText dataText={Locations_Text} />
            </ServiceItem>
            <ServiceItem>
              <ServicesImage
                src={CastingIcon}
                $imgWidth={'166px'}
                alt="Casting"
              />
              <RichText dataText={Casting_Text} />
            </ServiceItem>
            <ServiceItem>
              <ServicesImage
                src={PermitsIcon}
                $imgWidth={'152px'}
                alt="Permits"
              />
              <RichText dataText={Permits_Text} />
            </ServiceItem>
            <ServiceItem $itemHeight={'307px'}>
              <ServicesImage src={RentIcon} $imgWidth={'160px'} alt="Rent" />
              <RichText dataText={Rent_Text} />
            </ServiceItem>
            <ServiceItem>
              <ServicesImage src={CrewIcon} $imgWidth={'196px'} alt="Crew" />
              <RichText dataText={Crew_Text} />
            </ServiceItem>
            <ServiceItem>
              <ServicesImage
                src={InsuranceIcon}
                $imgWidth={'210px'}
                alt="Insurance"
              />
              <RichText dataText={Insurance_Text} />
            </ServiceItem>
            <ServiceItem $maxWidth={'425px'} $itemHeight={'323px'}>
              <ServicesImage
                src={AllIcon}
                $imgWidth={'239px'}
                alt="All Services"
              />
              <RichText dataText={All_Text} />
            </ServiceItem>
          </ServicesContent>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ModalProductionServices;
